
import { useCallback } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { WizardFormData, WizardState, WizardActions } from './types';
import { useProviderSubmit } from './submission/useProviderSubmit';
import { useUserSubmit } from './submission/useUserSubmit';
import { useLeadAccountSubmit } from './submission/useLeadAccountSubmit';
import { useStrategySubmit } from './submission/useStrategySubmit';
import { useAccountSubmit } from './submission/useAccountSubmit';

export const useWizardSubmit = (
  state: WizardState,
  actions: WizardActions
) => {
  const { formData } = state;
  const { setIsSubmitting } = actions;
  const { toast } = useToast();
  
  // Import all submission hooks
  const { createProvider } = useProviderSubmit();
  const { handleUserAssignment } = useUserSubmit();
  const { createLeadAccount } = useLeadAccountSubmit();
  const { createStrategy } = useStrategySubmit();
  const { createAccount } = useAccountSubmit();

  const handleFinalSubmit = useCallback(async (): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      // Step 1: Create Provider
      const providerId = await createProvider(formData);
      if (!providerId) {
        throw new Error('Failed to create provider');
      }
      
      // Step 2: Handle User Assignment
      await handleUserAssignment(formData, providerId);
      
      // Step 3: Create Lead Account
      const leadAccountId = await createLeadAccount(formData, providerId);
      if (!leadAccountId) {
        throw new Error('Failed to create lead account');
      }
      
      // Step 4: Create Strategy
      const strategyId = await createStrategy(formData, providerId);
      if (!strategyId) {
        throw new Error('Failed to create strategy');
      }
      
      // Step 5: Create Account
      await createAccount(formData, leadAccountId, strategyId);
      
      toast({
        title: "Success",
        description: "Provider integration created successfully",
      });
      
      return true;
    } catch (error) {
      console.error('Error submitting form:', error);
      
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create provider integration",
        variant: "destructive",
      });
      
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData, 
    setIsSubmitting, 
    toast, 
    createProvider, 
    handleUserAssignment, 
    createLeadAccount, 
    createStrategy, 
    createAccount
  ]);

  return { handleFinalSubmit };
};
